import { GetGroupInfo, GetMe } from '@/api/'
import {initSignalR, stop} from '@/plugins/signalr'

/** Strip list claims so they are never kept on state.user (lists come from GET /me only). */
function userWithoutListClaims (data) {
  const user = { ...data }
  if (user.group_list) {
    delete user.group_list
  }
  if (user.report_list) {
    delete user.report_list
  }
  return user
}

const state = () => ({
  user: {},
  group: {},
  group_list: [],
  report_list: [],
  groupLoading: false,
  pagePermit: [],
  pageList: [
    { title: 'Screening', icon: 'mdi-hospital-building', navigate: 'SCREENING', page: 'SCREENING', order: 1},
    { title: 'Physician', icon: 'mdi-stethoscope', navigate: 'DOCTOR_ROOM', page: 'DOCTOR_ROOM', order: 2 },
    { title: 'Pharmacy', icon: 'mdi-pill', navigate: 'PHARMACY', page: 'PHARMACY', order: 3},
    { title: 'Laboratory', icon: 'mdi-water-outline', navigate: 'LABORATORY', page: 'LABORATORY', order: 4},
    { title: 'Patient', icon: 'mdi-account-details', navigate: 'PATIENT', page: 'PATIENT', order: 5},
    { title: 'Inventory', icon: 'mdi-inbox-multiple', navigate: 'INVENTORY', page: 'INVENTORY', order: 6},
    { title: 'Sets', icon: 'mdi-package-variant-closed', navigate: 'CHECKING', page: 'CHECKING', order: 7},
    { title: 'Shift', icon: 'mdi-account-supervisor', navigate: 'SHIFT', page: 'SHIFT', order: 8},
    { title: 'Report', icon: 'mdi-file-chart', navigate: 'REPORT', page: 'REPORT', order: 9},
    { title: 'Tele-visit', icon: 'mdi-video', navigate: 'TELE', page: 'TELE', order: 10},
    { title: 'Visit', icon: 'mdi-clipboard-text', navigate: 'VISIT', page: 'VISIT', order: 11},
    { title: 'Queue', icon: 'mdi-format-list-bulleted', navigate: 'QUEUE', page: 'QUEUE', order: 12},
    { title: 'My Profile', icon: 'mdi-account-box', navigate: 'PROFILE', page: 'PROFILE', order: 13 },
    { title: 'Company Settings', icon: 'mdi-cogs', navigate: 'SETTING', page: 'SETTING', order: 14},
    { title: 'Help + Support', icon: 'mdi-face-agent', navigate: 'SUPPORT', page: 'SUPPORT', order: 15},
    { title: 'Administrator', icon: 'mdi-cancel', navigate: 'ADMIN', page: 'ADMIN', order: 16}
  ],
  landingPage: {},
  navItems: [],
  firstTime: true,
  canBook: '',
  currentGroupStock: {}
})

const mutations = {
  SET_USER (state, data) {
    state.user = data
  },
  SET_FIRST_TIME (state, data) {
    state.firstTime = data
  },
  SET_CAN_BOOK (state, data) {
    state.canBook = '' 
    if (data) {
      for (let i = 0; i < data.length; i++) {
        state.canBook += data[i].user_group + '|'
      }
    }
  },
  SET_CURRENT_GROUP_STOCK (state, data) {
    state.currentGroupStock = data
  },
  SET_GROUP_LOADING (state, data) {
    state.groupLoading = data
  },
  SET_GROUP_LIST (state, data) {
    if (!Array.isArray(data)) {
      state.group_list = []
      return
    }
    try {
      state.group_list = data.map((element) =>
        typeof element === 'string' ? JSON.parse(element) : element
      )
    } catch {
      state.group_list = []
    }
  },
  SET_REPORT_LIST (state, data) {
    if (!Array.isArray(data)) {
      state.report_list = []
      return
    }
    try {
      state.report_list = data.map((element) =>
        typeof element === 'string' ? JSON.parse(element) : element
      )
    } catch {
      state.report_list = []
    }
  },
  SET_GROUP (state, data) {
    state.group = data
  },
  SET_NAVIGATION_ITEMS (state, landingPage) {
    state.navItems = []
    const roles = (state.user.roles || []).filter((r) => r)
    state.landingPage = {}
    state.pageList.forEach((page) => {
      if (roles.includes(page.page)) {
        state.navItems.push(page)
      }
      if (landingPage && page.page === landingPage) {
        state.landingPage = page
      }
    })
    if (!state.landingPage.navigate) {
      const preferred = landingPage
        ? state.pageList.find((p) => p.page === landingPage)
        : null
      state.landingPage = preferred || state.navItems[0] || {}
    }
  },
  SET_PAGE_PERMIT (state, data) {
    state.pagePermit = data.split('|').filter(i => i)
  },
  SET_USER_AVATAR (state, data) {
    state.user.img_user = data
  }
}

const actions = {
  CHECK_PAGE_PERMIT ({ commit, state }, to) {
    for (let i = 0; i < state.navItems.length; i++) {
      if (state.navItems[i].navigate.includes(to.name)) {
        return true
      }
    }
    return false
  },
  SET_FIRST_TIME ({ commit }, value) {
    commit('SET_FIRST_TIME', value)
  },
  GET_DEFAULT_PAGE  ({ commit, state }) {
    return state.navItems[0]
  },
  GET_GROUP ({ commit }, groupId) {
    return new Promise((resolve, reject) => {
      GetGroupInfo({ groupId: groupId }, message => {
        if (message.data) {
          commit('SET_GROUP', message.data[0])
          commit('SET_CAN_BOOK', message.data[0].user_can_book)
          commit('SET_CURRENT_GROUP_STOCK', message.data[0].group_stocks[0])
          stop()
          initSignalR()
          resolve(message.data)
        } else {
          reject(new Error('not found'))
        }
      }, error => {
        reject(error)
      })
    })
  },
  /**
   * @param {{ profile: object, includeLists?: boolean } | object} options
   *   includeLists: true only for GET /me — never use group_list/report_list from token claims.
   */
  SET_USER ({ commit, state, dispatch }, options) {
    const fromMe = options && options.profile != null
    const data = fromMe ? options.profile : options
    const includeLists = fromMe ? !!options.includeLists : false

    return new Promise((resolve, reject) => {
      commit('SET_GROUP_LOADING', true)
      commit('SET_USER', userWithoutListClaims(data))
      if (includeLists) {
        commit('SET_GROUP_LIST', data.group_list || [])
        commit('SET_REPORT_LIST', data.report_list || [])
      } else {
        commit('SET_GROUP_LIST', [])
        commit('SET_REPORT_LIST', [])
      }
      commit('SET_NAVIGATION_ITEMS', data.landing_page)
      dispatch('User/GET_GROUP', (state.group.group_id) ? state.group.group_id : data.group_id, { root: true }).then((message) => {
        commit('SET_GROUP_LOADING', false)
        resolve(message)
      }).catch((error) => {
        commit('SET_GROUP_LOADING', false)
        reject(error)
      })
    })
  },
  /** Profile from GET /me; token claims only for other fields if /me fails (never list claims). */
  LOAD_PROFILE ({ commit, dispatch }, fallbackClaims) {
    commit('SET_GROUP_LOADING', true)
    return new Promise((resolve, reject) => {
      GetMe(
        (response) => {
          const payload = response.data
          if (!payload || typeof payload !== 'object') {
            if (fallbackClaims) {
              dispatch('SET_USER', { profile: fallbackClaims, includeLists: false }).then(resolve).catch(reject)
            } else {
              commit('SET_GROUP_LOADING', false)
              reject(new Error('Invalid /me response'))
            }
            return
          }
          dispatch('SET_USER', { profile: payload, includeLists: true }).then(resolve).catch(reject)
        },
        () => {
          if (fallbackClaims) {
            dispatch('SET_USER', { profile: fallbackClaims, includeLists: false }).then(resolve).catch(reject)
          } else {
            commit('SET_GROUP_LOADING', false)
            reject(new Error('GET /me failed'))
          }
        }
      )
    })
  }
}

const getters = {
  landingRouteName (state) {
    if (state.landingPage && state.landingPage.navigate) {
      return state.landingPage.navigate
    }
    if (state.navItems.length > 0) {
      return state.navItems[0].navigate
    }
    return null
  },
  userRoles (state) {
    return (state.user.roles || []).filter((r) => r)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
